import React, { useState, useEffect, useRef, useCallback, useSyncExternalStore } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { ScrollArea } from '../components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  Smartphone,
  MonitorPlay,
  Wifi,
  WifiOff,
  RefreshCw,
  Terminal,
  ArrowLeft,
  X,
  Search,
  ArrowDownToLine,
  Pause,
  PlugZap,
  Trash2,
  Filter,
  AlertTriangle,
  Maximize2,
  ChevronLeft,
  ChevronRight,
  Zap,
} from 'lucide-react';
import { bmPresenceApi, bmLogViewUrl, devicesApi, slotsApi, bmVideoApi } from '../lib/api';
import { buildRowIndex, buildPresenceIndex, associatePhone, LOAD } from '../lib/monitorAssoc';
import MonitorPhoneAssoc from '../components/MonitorPhoneAssoc';
import StreamStateChip, { useStreamStatus } from '../components/StreamStateChip';
import { streamManager, attachVisibilityAutostop } from '../lib/streamManager';
import { WSSCRCPY_BASE, SPRING_API_BASE, isWebCodecsSupported } from '../lib/scrcpyStream';
import { getBridgeHealth, setDeviceEngine, bridgeIsLocal, setResolvedTransport, ENGINE_MODES } from '../lib/bridgeApi';
import {
  resolveMonitorConfig, chooseRoute, probeLocalRelay, clearStoredOverrides, CONFIG_TIMEOUT_MS,
} from '../lib/monitorConfig';
import { wantsRepair } from '../lib/monitorNotes';
import PageHeader from '../components/PageHeader';
import { Slider } from '../components/ui/slider';
import usePageTitle from '../hooks/usePageTitle';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

/*
 * Aurora pass (2026-08-23): PageHeader, semantic tokens, a responsive phone grid (2 columns on a
 * phone, then as many PHONE_W columns as fit), and an expand dialog that stacks screen-over-logs
 * under md. The stream / canvas / SSE logic and every element the stream attaches to are untouched.
 */

// ==================== Constants ====================

const PHONE_W = 190;
const PHONE_H = Math.round(PHONE_W * 2.1);

// The phone frame keeps the PHONE_W:PHONE_H aspect whatever width the grid column gives it; the
// canvas inside is 100% x 100% with object-fit: contain (streamManager), so it follows the frame.
const PHONE_ASPECT = `${PHONE_W} / ${PHONE_H}`;

// Pagination: the user bounds bandwidth by choosing how many phones stream at once.
// EVERY phone on the current page streams continuously at LOW (no viewport gating).
const PER_PAGE_OPTIONS = [6, 12, 24, 48];
const DEFAULT_PER_PAGE = 12;
const LS_PER_PAGE = 'necro.monitor.perPage';
const LS_PHONE_W = 'necro.monitor.phoneW';
/*
 * The grid is auto-fill, so these are MINIMUM column widths, not fixed ones - the phones still
 * stretch to fill the row.
 *
 * Discrete stops rather than a uniform step, because the two directions are not equally useful.
 * Going smaller is how you get more phones on screen at once, and every 10px there changes the
 * column count on a wide monitor - so the stops below the default are close together. Going
 * bigger only ever means "look at this one properly", which needs far less precision, so those
 * stops widen out. The ceiling is 2x the default deliberately: past that the grid is one or two
 * phones and the expand view is the better tool.
 */
const PHONE_W_STOPS = [100, 110, 120, 130, 140, 150, 160, 170, 180, 190, 215, 245, 280, 320, 380];

/** Stable per-phone key: the same phone serial can appear under different PCs. */
const keyOf = (d) => `${d.installId}::${d.serial}`;

function loadPerPage() {
  try {
    const v = parseInt(window.localStorage.getItem(LS_PER_PAGE), 10);
    if (PER_PAGE_OPTIONS.includes(v)) return v;
  } catch (e) { /* ignore */ }
  return DEFAULT_PER_PAGE;
}

/** Exponential backoff: 1s, 2s, 4s, 8s, 16s, capped at 30s. Cosmetic now that EventSource
 *  reconnects natively; used only for the rare manual-reopen after a fatal error. */
function getBackoffDelay(attempt) {
  return Math.min(1000 * Math.pow(2, attempt), 30000);
}

const MAX_RECONNECT_ATTEMPTS = 20;
const MAX_LOG_ENTRIES = 500;
const VISIBLE_WINDOW = 200; // Only render last N logs for performance

const WEBCODECS_OK = isWebCodecsSupported();

// ---- Monitor video self-heal (docs/MONITOR-VIDEO-SELFHEAL-DESIGN.md section 3) ----
// Refresh reopens the page's streams at most once per 5 s (review should-fix 11: repeated clicks
// must not churn unsubscribe/subscribe on the customer's PC).
const REOPEN_DEBOUNCE_MS = 5000;
/*
 * Review MF3 / E2E D-1: a repair is sent only after the tiles it is for have reopened their sockets (each tile reports
 * an opened state after the reopen), or at the latest this long after the reopen. Sent in the same tick as the reopen,
 * it reached a PC whose phones this page - their only viewer - had just unsubscribed, so the PC found no phone to
 * repair ("bridges 0/0 started; N phone(s) not subscribed") and the owner's D4 restart never ran.
 */
const REPAIR_AFTER_OPEN_MAX_MS = 2500;
const OPENED_STATES = new Set(['waiting', 'starting', 'streaming', 'no-picture']);

/** Resolves 'open' when every key's stream reports an opened socket set at or after `since`, else 'timeout' after maxMs. */
function whenTilesOpen(keys, since, maxMs) {
  return new Promise((resolve) => {
    let done = false;
    const unsubs = [];
    const isOpen = (k) => {
      const st = streamManager.getStatus(k);
      return !!st && OPENED_STATES.has(st.state) && (st.since == null || st.since >= since);
    };
    const finish = (why) => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      unsubs.forEach((u) => { try { u(); } catch (e) { /* noop */ } });
      resolve(why);
    };
    const check = () => { if (keys.every(isOpen)) finish('open'); };
    const timer = setTimeout(() => finish('timeout'), maxMs);
    for (const k of keys) unsubs.push(streamManager.subscribe(k, check));
    check();
  });
}
// A visible page with no pointer or key input for 30 min stops every stream (review M5): a Monitor
// left open overnight must not relay video through our server all night.
const IDLE_PAUSE_MS = 30 * 60 * 1000;
const IDLE_CHECK_MS = 30000;
const STATS_LOG_MS = 30000;
const AUTH_CHECK_GAP_MS = 60000;

/** The build-time transport, read the way DefinePlugin can replace it (dev harnesses only). */
function envTransport() {
  try {
    return process.env.REACT_APP_MONITOR_TRANSPORT;
  } catch (e) {
    return undefined;
  }
}

function describeOverrides(overrides) {
  const words = overrides.map((o) => (o.key === 'transport'
    ? String(o.value)
    : `${o.key} ${o.value ? 'on' : 'off'}`));
  return `Video mode: ${words.join(', ')} (set in this browser)`;
}

function routeWords(video) {
  if (!video || !video.ready) return '';
  if (video.route === 'remote') return 'Video: through the NecroMancer server';
  if (video.autoFallback) return 'Video: direct from this PC; phones on other PCs go through the server';
  return 'Video: direct from this PC';
}

/*
 * The honest header count (V3): "streaming" is tiles that show a picture, not sockets opened. Its
 * own component, subscribed to every tile's state, so a state change re-renders this text only and
 * never the 48 cards (which would re-home their canvases).
 */
function VideoCounts({ online, entries }) {
  useSyncExternalStore(streamManager.subscribeAll, streamManager.getVersion, streamManager.getVersion);
  const streaming = entries.filter((e) => {
    const st = streamManager.getStatus(e.key);
    return !!st && st.state === 'streaming';
  }).length;
  return <>{`${online} online · ${streaming} streaming · ${entries.length - streaming} without picture`}</>;
}

/** One toast per PC, in the server's (and the PC's) own words (design V4). */
function repairErrorText(status, data, name) {
  const d = data || {};
  const code = String(d.code || '').toUpperCase();
  const said = d.result || d.detail || '';
  if (status === 429 || code === 'RATE_LIMITED') {
    if (d.scope === 'user') {
      return `Video repair limit reached for this hour; try again in ${d.retryAfterSec || 60} s`;
    }
    return `${name}: repaired recently, try again in ${d.retryAfterSec || 60} s`;
  }
  if (code === 'CLIENT_TOO_OLD' || (status === 502 && /unknown op/i.test(said))) {
    return `${name}'s client is too old to repair video; it retries on its own after the next client update`;
  }
  if (code === 'REMOTE_CONTROLS_OFF' || (status === 502 && /remoteControls\s*=\s*false/i.test(said))) {
    return `Repair is turned off on ${name} (remoteControls=false)`;
  }
  if (status === 502) return `${name}: ${said || 'the PC refused the repair'}`;
  if (status === 504 || code === 'TIMEOUT') return `${name} did not answer the repair in time`;
  if (status === 409 || code === 'NOT_CONNECTED') return `${name}'s client is not connected`;
  if (status === 404) return `${name}: video repair is not available (${said || 'server'})`;
  if (status === 401) return 'Signed out, sign in again';
  if (status) return `${name}: ${said || `HTTP ${status}`}`;
  return `${name}: the repair request failed`;
}

/* Tailwind needs every class spelled out, so status tones are full strings (same shape as the
 * Dashboard's TONE map). Badges are rounded-full pills like the rest of Aurora. */
const CHIP = {
  success: 'rounded-full bg-success/15 text-success border-success/30',
  warning: 'rounded-full bg-warning/15 text-warning border-warning/30',
  danger: 'rounded-full bg-danger/15 text-danger border-danger/30',
  primary: 'rounded-full bg-primary/15 text-primary border-primary/30',
};

// ==================== Hooks ====================

/** Debounce a value by delay ms */
function useDebouncedValue(value, delay = 300) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

// ==================== Live screen mount ====================
//
// The streamManager owns one <canvas> per live phone (created once, kept alive for
// the whole time the phone is on the current page). A card just borrows that canvas
// into its own DOM node; the expand modal borrows the SAME canvas. Moving the node
// never touches the socket, so expand/collapse are pure in-place quality changes.

function StreamMount({ streamKey, streamsTick, className, style }) {
  const ref = useRef(null);
  useEffect(() => {
    const host = ref.current;
    if (!host) return undefined;
    const canvas = streamManager.getCanvas(streamKey);
    if (canvas) host.appendChild(canvas); // moves it here from wherever it was
    return () => {
      // Detach on unmount so the canvas can be re-homed (card <-> modal) cleanly.
      const c = streamManager.getCanvas(streamKey);
      if (c && c.parentNode === host) host.removeChild(c);
    };
    // streamsTick re-runs this once the socket/canvas actually exists.
  }, [streamKey, streamsTick]);
  return <div ref={ref} className={className} style={style} />;
}

// ==================== Phone Card Component (Grid View) ====================
//
/*
 * Which capture engine this phone streams through.
 *
 * Two engines are installed side by side on the customer's PC. The 2021 one streams BLACK frames
 * on Android 14+ (its hidden capture API was removed), so Auto sends 14+ to the modern engine -
 * but the legacy one is kept deliberately and is one click away for testing.
 *
 * Only rendered when the browser can actually reach the bridge (local transport, bridge present
 * and new enough). Otherwise nothing is shown at all, rather than a control that cannot work.
 */
const EnginePicker = ({ serial }) => {
  const [health, setHealth] = React.useState(null);
  const [busy, setBusy] = React.useState(false);

  const refresh = React.useCallback(() => {
    getBridgeHealth().then(setHealth);
  }, []);

  React.useEffect(() => {
    if (!bridgeIsLocal()) return;
    refresh();
  }, [refresh]);

  if (!health || !health.bridge) return null;

  const current = (health.overrides && health.overrides[serial]) || 'auto';
  const session = (health.sessions || []).find((x) => x.udid === serial);

  const choose = async (mode) => {
    if (mode === current || busy) return;
    setBusy(true);
    try {
      await setDeviceEngine(serial, mode);
      toast.success('Engine set to ' + mode, {
        description: 'Reopen this phone to stream on the new engine.',
      });
      refresh();
    } catch (e) {
      toast.error('Could not change engine', { description: String(e.message || e) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex items-center gap-1.5" data-testid="engine-picker">
      <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Engine</span>
      <div className="flex overflow-hidden rounded-full border border-border">
        {ENGINE_MODES.map((m) => (
          <button
            key={m.value}
            type="button"
            disabled={busy}
            title={m.hint}
            onClick={() => choose(m.value)}
            data-testid={'engine-' + m.value}
            className={cn(
              'px-2 py-0.5 text-[10px] font-medium transition-colors disabled:opacity-50',
              current === m.value
                ? 'bg-primary/20 text-primary'
                : 'text-muted-foreground hover:text-foreground'
            )}
          >
            {m.label}
          </button>
        ))}
      </div>
      {session && (
        <span className="text-[10px] text-muted-foreground" title="Engine actually streaming now">
          ({session.engine})
        </span>
      )}
    </div>
  );
};

// Live LOW screen for every phone on the page. Click opens the expand modal.
// While expanded, this card shows a placeholder because its canvas has moved to
// the modal (a DOM node can only live in one place).
//
// The frame is deliberately NOT an .aurora-card: up to 48 of these repaint at video rate and a
// backdrop-filter layer on each would be the most expensive thing on the page.

const PhoneCard = ({ device, assoc, onClick, isExpanded, streamsTick, video }) => {
  const displayName = device.model || device.device_name || device.hostname || 'Unknown';
  const online = device.state === 'device';
  const streamKey = keyOf(device);
  // Self-heal V3: with honest states on, the adb badge says "Online" (adb sees the phone) and the
  // chip on the screen says whether a picture is actually coming. Off = today's "Live".
  const honest = !!(video && video.states);

  const ScreenArea = () => {
    if (isExpanded) {
      return (
        <div className="absolute inset-0 flex items-center justify-center bg-background">
          <div className="text-center text-muted-foreground p-4">
            <Maximize2 className="h-8 w-8 mx-auto mb-2 text-primary" />
            <p className="text-xs">Expanded — streaming in HD</p>
          </div>
        </div>
      );
    }
    if (!online) {
      return (
        <div className="absolute inset-0 flex items-center justify-center bg-background">
          <div className="text-center text-muted-foreground/60 px-3">
            <WifiOff className="h-8 w-8 mx-auto mb-2 opacity-40" />
            <p className="text-[10px] leading-tight text-muted-foreground">{device.state}</p>
          </div>
        </div>
      );
    }
    if (!WEBCODECS_OK) {
      return (
        <div className="absolute inset-0 flex items-center justify-center bg-background">
          <div className="text-center text-muted-foreground/60 px-3">
            <AlertTriangle className="h-7 w-7 mx-auto mb-2 opacity-40" />
            <p className="text-[10px] leading-tight text-muted-foreground">Screen needs a WebCodecs browser</p>
          </div>
        </div>
      );
    }
    // Live LOW canvas.
    return <StreamMount streamKey={streamKey} streamsTick={streamsTick} className="absolute inset-0 bg-black" />;
  };

  const showVideoState = honest && online && WEBCODECS_OK && !isExpanded;

  return (
    <div
      className="relative group min-w-0 w-full cursor-pointer transition-transform hover:scale-[1.02]"
      onClick={online ? onClick : undefined}
      data-testid={`phone-card-${device.serial}`}
    >
      <div
        className={cn(
          'relative w-full overflow-hidden rounded-2xl border-2 bg-card transition-[border-color,box-shadow]',
          isExpanded ? 'border-primary/50 shadow-glow' : 'border-border group-hover:border-primary/40'
        )}
        style={{ aspectRatio: PHONE_ASPECT }}
      >
        {/* Header with device name */}
        <div className="absolute top-0 left-0 right-0 z-20 bg-gradient-to-b from-background/95 via-background/60 to-transparent px-2 pt-2 pb-6 pointer-events-none">
          <div className="flex items-center justify-between gap-1">
            <span className="min-w-0 text-xs font-medium text-foreground truncate">{displayName}</span>
            <Badge
              variant="outline"
              className={cn(
                'text-[10px] py-0 px-1.5 shrink-0',
                online ? CHIP.success : device.state === 'unauthorized' ? CHIP.warning : CHIP.danger
              )}
            >
              {online ? (honest ? 'Online' : 'Live') : device.state}
            </Badge>
          </div>
        </div>

        <ScreenArea />

        {showVideoState && video.idlePaused && (
          <div
            className="absolute inset-0 z-40 flex items-center justify-center bg-background/80 px-3"
            onClick={(e) => { e.stopPropagation(); if (video.onResume) video.onResume(); }}
            data-testid="tile-idle-paused"
          >
            <p className="text-center text-[10px] leading-tight text-muted-foreground">Paused to save bandwidth - click to resume</p>
          </div>
        )}
        {showVideoState && !video.idlePaused && (
          <StreamStateChip
            streamKey={streamKey}
            pc={device.hostname}
            repairEnabled={video.repair}
            repairBlockedUntil={video.repairUntil ? video.repairUntil[device.installId] : 0}
            onRepair={() => video.onRepair && video.onRepair(device)}
          />
        )}

        {/* Hover overlay - click to expand */}
        {online && !isExpanded && (
          <div className="absolute inset-0 z-30 cursor-pointer" onClick={(e) => { e.stopPropagation(); onClick?.(); }}>
            <div className="absolute inset-0 bg-primary/0 group-hover:bg-primary/10 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
              <div className="rounded-full bg-background/80 ring-1 ring-border px-3 py-2 flex items-center gap-2">
                <Maximize2 className="h-4 w-4 text-primary" />
                <span className="text-sm text-foreground">Expand + logs</span>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="mt-2 min-w-0 text-center">
        <p className="text-xs text-muted-foreground font-mono truncate">{device.serial}</p>
        {device.hostname && <p className="text-[10px] text-muted-foreground/60 truncate">{device.hostname}</p>}
        {/* The device row this phone is and the slots on it. Two lines are reserved even while
            loading, so the grid does not jump when /uiapi/devices and /uiapi/slots answer. */}
        <MonitorPhoneAssoc assoc={assoc} className="mt-0.5 min-h-8" />
      </div>
    </div>
  );
};

// ==================== Expanded View Modal (HD screen + log consumer) ====================
//
// Left pane: the SAME live stream as the grid card, raised to HIGH in place on its
// existing socket (streamManager.setQuality(key,'high')) when this opens, and lowered
// back to LOW on close. Right pane: the live console log SSE stream (unchanged).

const ExpandedPhoneView = ({ device, assoc, isOpen, onClose, streamsTick, video }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [logs, setLogs] = useState([]);
  const [wsConnected, setWsConnected] = useState(false);
  const [reconnectCount, setReconnectCount] = useState(0);
  const [uplinkStatus, setUplinkStatus] = useState(null); // 'online' | 'offline' | 'gone'
  const [autoScroll, setAutoScroll] = useState(true);
  const [levelFilter, setLevelFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [lastMessageTime, setLastMessageTime] = useState(null);
  const [reconnectNonce, setReconnectNonce] = useState(0);
  const logsEndRef = useRef(null);
  const esRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  // Highest log seq already applied for the CURRENT stream. Now that the relay is at-least-once on
  // reconnect (BM re-sends unacked frames from its ring), a resumed stream can replay a line we
  // already have; drop any frame whose seq <= this. Reset to -1 whenever the stream changes.
  const lastSeqRef = useRef(-1);

  // Debounce search query for performance
  const debouncedSearchValue = useDebouncedValue(searchQuery, 250);

  const displayName = device ? (device.model || device.device_name || device.hostname || 'Unknown') : '';
  const online = device && device.state === 'device';
  const streamKey = device ? keyOf(device) : null;
  // The engine picker talks to the relay on THIS PC, so it only belongs on a phone streamed direct.
  const streamStatus = useStreamStatus(isOpen ? streamKey : null);
  const streamedDirect = !streamStatus || streamStatus.route !== 'remote';
  const honest = !!(video && video.states);

  // SCREEN: raise this phone to HIGH in place while expanded, LOW again on close.
  // The socket is owned by the page-level syncPage effect and stays open throughout,
  // so this is a pure sendNewVideoSetting on the existing connection (no reconnect).
  useEffect(() => {
    if (!isOpen || !streamKey || !online) return undefined;
    streamManager.setQuality(streamKey, 'high');
    return () => {
      streamManager.setQuality(streamKey, 'low');
    };
  }, [isOpen, streamKey, online]);

  // Auto-scroll logs (only when enabled)
  useEffect(() => {
    if (autoScroll && logsEndRef.current) {
      logsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [logs, autoScroll]);

  // Detect user scroll to auto-pause autoscroll
  const handleScroll = useCallback((e) => {
    const el = e.target;
    if (!el) return;
    const isAtBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 60;
    if (!isAtBottom && autoScroll) {
      setAutoScroll(false);
    } else if (isAtBottom && !autoScroll) {
      setAutoScroll(true);
    }
  }, [autoScroll]);

  // Manual reconnect: bump the nonce so the stream effect tears down and re-opens.
  const handleReconnect = useCallback(() => {
    setReconnectNonce(n => n + 1);
  }, []);

  // Connect to the SSE log stream
  useEffect(() => {
    if (!isOpen || !device || device.installId == null) return;

    const installId = device.installId;
    let es = null;
    let closedByUs = false;
    let attempt = 0;

    // Tolerant of both the BM wire shape {seq,ts,line} and V2-style {timestamp,level,message,module}.
    const normalizeLog = (raw) => ({
      timestamp: raw.ts != null ? new Date(raw.ts).toISOString() : (raw.timestamp || new Date().toISOString()),
      level: raw.level || (raw.is_error ? 'error' : 'info'),
      message: raw.line != null ? raw.line : (raw.message || raw.logs || ''),
      module: raw.module || raw.slot || '',
    });

    const appendLog = (entry) => setLogs(prev => [...prev.slice(-(MAX_LOG_ENTRIES - 1)), entry]);

    const openStream = () => {
      if (closedByUs) return;
      const url = bmLogViewUrl(installId, device.serial);
      setWsConnected(false);
      es = new EventSource(url, { withCredentials: true });
      esRef.current = es;

      es.onopen = () => {
        setIsLoading(false);
        setWsConnected(true);
        setReconnectCount(0);
        attempt = 0;
      };

      // First frame + on change: where the uplink stands right now.
      es.addEventListener('status', (e) => {
        setLastMessageTime(new Date());
        try {
          const data = JSON.parse(e.data);
          if (data.uplink) setUplinkStatus(data.uplink);
        } catch (err) { /* ignore malformed frame */ }
      });

      // A single live log line. Dedup on seq: the relay is at-least-once across a reconnect, so a
      // resumed stream can replay lines we already applied. The seq is monotonic per install; drop
      // anything at or below the highest we have shown for this stream (lastSeqRef resets on change).
      es.addEventListener('log', (e) => {
        setLastMessageTime(new Date());
        try {
          const raw = JSON.parse(e.data);
          const seq = typeof raw.seq === 'number' ? raw.seq : Number(e.lastEventId);
          if (Number.isFinite(seq)) {
            if (seq <= lastSeqRef.current) return; // already seen this line (or an older one)
            lastSeqRef.current = seq;
          }
          appendLog(normalizeLog(raw));
        } catch (err) { /* ignore malformed frame */ }
      });

      // Relay dropped frames for a slow viewer - surface it as a warn line.
      es.addEventListener('gap', (e) => {
        try {
          const data = JSON.parse(e.data);
          const dropped = data.dropped || 0;
          if (dropped > 0) {
            appendLog({
              timestamp: new Date().toISOString(),
              level: 'warn',
              message: `${dropped} log line${dropped !== 1 ? 's' : ''} dropped (slow connection)`,
              module: 'stream',
            });
          }
        } catch (err) { /* ignore malformed frame */ }
      });

      es.onerror = () => {
        setWsConnected(false);
        if (closedByUs) return;
        // EventSource reconnects itself while readyState === CONNECTING; reflect that as a retry.
        attempt = Math.min(attempt + 1, MAX_RECONNECT_ATTEMPTS);
        setReconnectCount(attempt);
        // On a fatal close (e.g. transient network drop the browser gave up on) reopen manually.
        if (es && es.readyState === EventSource.CLOSED) {
          try { es.close(); } catch (err) { /* noop */ }
          if (attempt < MAX_RECONNECT_ATTEMPTS) {
            reconnectTimeoutRef.current = setTimeout(openStream, getBackoffDelay(attempt));
          }
        }
      };
    };

    openStream();

    return () => {
      closedByUs = true;
      if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
      if (es) { try { es.close(); } catch (err) { /* noop */ } }
      esRef.current = null;
      setWsConnected(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, device?.installId, device?.serial, reconnectNonce]);

  // Reset logs when device changes (i.e. when the stream changes). The seq cursor must reset here
  // too, so the first line of the newly selected phone is never dropped as a "duplicate".
  useEffect(() => {
    setLogs([]);
    setIsLoading(true);
    setAutoScroll(true);
    setLevelFilter('all');
    setSearchQuery('');
    setReconnectCount(0);
    setUplinkStatus(null);
    lastSeqRef.current = -1;
  }, [device?.installId, device?.serial]);

  // Filter logs (uses debounced search for performance)
  const filteredLogs = React.useMemo(() => {
    let result = logs;
    if (levelFilter !== 'all') {
      result = result.filter(log => {
        const lvl = (log.level || '').toLowerCase();
        if (levelFilter === 'error') return lvl === 'error';
        if (levelFilter === 'warn') return lvl === 'warn' || lvl === 'warning';
        if (levelFilter === 'info') return lvl === 'info';
        if (levelFilter === 'debug') return lvl === 'debug';
        return true;
      });
    }
    if (debouncedSearchValue.trim()) {
      const q = debouncedSearchValue.toLowerCase();
      result = result.filter(log =>
        (log.message || '').toLowerCase().includes(q) ||
        (log.module || '').toLowerCase().includes(q)
      );
    }
    return result;
  }, [logs, levelFilter, debouncedSearchValue]);

  // Windowed rendering: only render the last VISIBLE_WINDOW entries for DOM performance
  const visibleLogs = React.useMemo(() => {
    if (filteredLogs.length <= VISIBLE_WINDOW) return filteredLogs;
    return filteredLogs.slice(-VISIBLE_WINDOW);
  }, [filteredLogs]);

  const hiddenCount = filteredLogs.length - visibleLogs.length;

  // Count by level
  const levelCounts = React.useMemo(() => {
    const counts = { all: logs.length, info: 0, warn: 0, error: 0, debug: 0 };
    logs.forEach(log => {
      const lvl = (log.level || '').toLowerCase();
      if (lvl === 'error') counts.error++;
      else if (lvl === 'warn' || lvl === 'warning') counts.warn++;
      else if (lvl === 'debug') counts.debug++;
      else counts.info++;
    });
    return counts;
  }, [logs]);

  if (!device) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      {/* Near-full-viewport monitor dialog: screen pane over the logs under md, side by side from md.
          The component's own corner X is hidden ([&>button:last-child]) - the header renders the close. */}
      <DialogContent className="flex h-[90dvh] max-h-[90dvh] w-[calc(100vw-1rem)] max-w-[calc(100vw-1rem)] flex-col gap-0 overflow-hidden rounded-2xl border-border bg-card p-0 sm:w-[96vw] sm:max-w-[96vw] sm:rounded-2xl [&>button:last-child]:hidden">
        {/* Header */}
        <DialogHeader className="shrink-0 space-y-0 border-b border-border px-3 py-2.5 text-left sm:px-4 sm:py-3">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="tap text-muted-foreground hover:text-foreground"
              data-testid="back-to-grid-btn"
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to Grid
            </Button>
            <Smartphone className="hidden h-5 w-5 shrink-0 text-primary sm:block" />
            <div className="min-w-0 flex-1 basis-40">
              <DialogTitle className="truncate font-heading text-base text-foreground sm:text-lg">{displayName}</DialogTitle>
              <p className="truncate text-xs text-muted-foreground font-mono">{device.serial}</p>
              <MonitorPhoneAssoc assoc={assoc} cap={8} align="start" className="mt-0.5" />
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {online && (
                <Badge variant="outline" className={CHIP.primary} data-testid="hd-badge">
                  <Zap className="h-3 w-3 mr-1" /> HD stream
                </Badge>
              )}
              {/* Uplink (desktop-client) presence, from the SSE status frame */}
              <Badge
                variant="outline"
                className={
                  uplinkStatus === 'online'
                    ? CHIP.success
                    : uplinkStatus === 'gone'
                    ? CHIP.danger
                    : CHIP.warning
                }
              >
                {uplinkStatus === 'online' ? <><Wifi className="h-3 w-3 mr-1" /> Client online</>
                  : uplinkStatus === 'gone' ? <><WifiOff className="h-3 w-3 mr-1" /> Revoked</>
                  : <><WifiOff className="h-3 w-3 mr-1" /> Client offline</>}
              </Badge>
              {streamedDirect && <EnginePicker serial={device.serial} />}
            </div>
            <div className="ml-auto flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={onClose}
                className="tap text-muted-foreground hover:text-foreground"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </DialogHeader>

        {/* Main content - HD screen + Logs */}
        <div className="flex min-h-0 flex-1 flex-col md:flex-row">
          {/* HD live screen pane (same stream, raised to HIGH) */}
          <div className="relative flex h-[38vh] w-full shrink-0 flex-col items-center justify-center border-b border-border bg-black p-2 md:h-auto md:w-[35%] md:min-w-[300px] md:max-w-[440px] md:border-b-0 md:border-r md:p-3">
            {online && WEBCODECS_OK ? (
              <>
                <StreamMount
                  streamKey={streamKey}
                  streamsTick={streamsTick}
                  className="w-full h-full flex items-center justify-center"
                  style={{ minHeight: 0 }}
                />
                {honest && !video.idlePaused && (
                  <StreamStateChip
                    streamKey={streamKey}
                    pc={device.hostname}
                    variant="dialog"
                    repairEnabled={video.repair}
                    repairBlockedUntil={video.repairUntil ? video.repairUntil[device.installId] : 0}
                    onRepair={() => video.onRepair && video.onRepair(device)}
                  />
                )}
              </>
            ) : (
              <div className="text-center px-6">
                <div className="p-4 rounded-full bg-secondary/60 w-fit mx-auto mb-3">
                  <MonitorPlay className="h-10 w-10 text-muted-foreground/60" />
                </div>
                <p className="text-sm text-foreground/90 font-medium">
                  {online ? 'Screen needs a WebCodecs browser' : 'Phone offline'}
                </p>
              </div>
            )}
          </div>

          {/* Automation Logs Panel */}
          <div className="flex min-h-0 min-w-0 flex-1 flex-col bg-background" data-testid="logs-panel">
            {/* Row 1: Title + Connection + Actions */}
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 border-b border-border px-3 py-2 sm:px-4 sm:py-2.5">
              <Terminal className="h-4 w-4 text-primary shrink-0" />
              <span className="font-heading text-sm font-semibold text-foreground">Automation Logs</span>

              <Badge
                variant="outline"
                className={cn('text-[10px] ml-1', wsConnected
                  ? CHIP.success
                  : reconnectCount >= MAX_RECONNECT_ATTEMPTS
                    ? CHIP.danger
                    : CHIP.warning)}
                data-testid="ws-connection-badge"
              >
                {wsConnected
                  ? 'LIVE'
                  : reconnectCount >= MAX_RECONNECT_ATTEMPTS
                    ? 'Disconnected'
                    : reconnectCount > 0
                      ? `Retry ${reconnectCount}/${MAX_RECONNECT_ATTEMPTS}`
                      : 'Connecting...'}
              </Badge>

              {lastMessageTime && (
                <span className="text-[10px] text-muted-foreground/60 tnum hidden lg:inline">
                  Last: {lastMessageTime.toLocaleTimeString()}
                </span>
              )}

              <div className="flex-1" />

              {!wsConnected && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleReconnect}
                  className="text-warning hover:text-warning/80 h-6 text-xs px-2"
                  data-testid="reconnect-btn"
                >
                  <PlugZap className="h-3 w-3 mr-1" />
                  Reconnect
                </Button>
              )}

              <Button
                variant="ghost"
                size="sm"
                onClick={() => setAutoScroll(!autoScroll)}
                className={cn('h-6 text-xs px-2', autoScroll ? 'text-primary hover:text-primary/80' : 'text-muted-foreground hover:text-foreground')}
                data-testid="autoscroll-toggle"
                title={autoScroll ? 'Autoscroll ON - click to pause' : 'Autoscroll OFF - click to resume'}
              >
                {autoScroll ? <ArrowDownToLine className="h-3 w-3 mr-1" /> : <Pause className="h-3 w-3 mr-1" />}
                {autoScroll ? 'Auto' : 'Paused'}
              </Button>

              <span className="text-[10px] text-muted-foreground tnum" data-testid="log-count">
                {filteredLogs.length !== logs.length
                  ? `${filteredLogs.length}/${logs.length}`
                  : logs.length}
                {hiddenCount > 0 && ` (${hiddenCount} older hidden)`}
              </span>

              <Button
                variant="ghost"
                size="sm"
                onClick={() => setLogs([])}
                className="tap text-muted-foreground hover:text-foreground h-6 text-xs px-2"
                aria-label="Clear logs"
                title="Clear logs"
                data-testid="clear-logs-btn"
              >
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>

            {/* Row 2: Filters */}
            <div className="flex flex-wrap items-center gap-2 border-b border-border/50 px-3 py-2 sm:px-4">
              <div className="flex flex-wrap items-center gap-1" data-testid="level-filters">
                {[
                  { key: 'all', label: 'All', color: 'text-foreground/90 bg-secondary border-border' },
                  { key: 'error', label: 'Error', color: 'text-danger bg-danger/10 border-danger/30', count: levelCounts.error },
                  { key: 'warn', label: 'Warn', color: 'text-warning bg-warning/10 border-warning/30', count: levelCounts.warn },
                  { key: 'info', label: 'Info', color: 'text-success bg-success/10 border-success/30', count: levelCounts.info },
                  { key: 'debug', label: 'Debug', color: 'text-muted-foreground bg-secondary border-border', count: levelCounts.debug },
                ].map(f => (
                  <button
                    key={f.key}
                    type="button"
                    onClick={() => setLevelFilter(f.key)}
                    aria-pressed={levelFilter === f.key}
                    className={cn(
                      'rounded-full border px-2.5 py-0.5 text-[10px] font-medium tnum transition-all',
                      levelFilter === f.key
                        ? f.color + ' ring-1 ring-offset-1 ring-offset-background ring-current'
                        : 'text-muted-foreground bg-background border-border hover:text-foreground hover:border-muted-foreground/40'
                    )}
                    data-testid={`filter-${f.key}`}
                  >
                    {f.label}{f.count > 0 ? ` (${f.count})` : ''}
                  </button>
                ))}
              </div>

              <div className="flex-1" />

              <div className="relative w-full sm:w-48" data-testid="search-container">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search logs..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full rounded-full border border-border bg-secondary/60 py-1 pl-7 pr-7 text-[11px] text-foreground/90 placeholder:text-muted-foreground/70 focus:border-primary/50 focus:outline-none"
                  data-testid="search-input"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery('')}
                    aria-label="Clear search"
                    className="absolute right-1 top-1/2 -translate-y-1/2 inline-flex h-5 w-5 items-center justify-center rounded-full text-muted-foreground hover:text-foreground"
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </div>
            </div>

            {/* Logs Content */}
            <ScrollArea className="min-h-0 flex-1" onScrollCapture={handleScroll}>
              <div className="p-2 space-y-0 font-mono text-[11px] leading-tight" data-testid="logs-content">
                {visibleLogs.length > 0 ? (
                  <>
                    {hiddenCount > 0 && (
                      <div className="text-center py-1 text-[10px] text-muted-foreground/60 border-b border-border/30">
                        {hiddenCount} older entries not shown (rendering last {VISIBLE_WINDOW})
                      </div>
                    )}
                    {visibleLogs.map((log, i) => (
                      <div key={i} className="text-muted-foreground py-0.5 border-b border-border/30 hover:bg-secondary/40" data-testid="log-entry">
                        <span className="text-primary/70">[{log.timestamp?.split('T')[1]?.substring(0, 8) || log.time}]</span>{' '}
                        <span className={cn('font-medium',
                          log.level === 'ERROR' || log.level === 'error' ? 'text-danger' :
                          log.level === 'WARNING' || log.level === 'warn' || log.level === 'warning' ? 'text-warning' :
                          log.level === 'DEBUG' || log.level === 'debug' ? 'text-muted-foreground/60' :
                          'text-success'
                        )}>
                          [{(log.level || 'info').toUpperCase()}]
                        </span>{' '}
                        <span className="text-foreground/90 whitespace-pre-wrap break-words">{log.message}</span>
                        {log.module && (
                          <span className="text-muted-foreground/60 ml-2">({log.module})</span>
                        )}
                      </div>
                    ))}
                    <div ref={logsEndRef} />
                  </>
                ) : logs.length > 0 && filteredLogs.length === 0 ? (
                  <div className="text-muted-foreground/60 text-center py-12">
                    <Filter className="h-8 w-8 mx-auto mb-2 opacity-30" />
                    <p className="text-sm">No logs match current filters</p>
                    <p className="text-xs mt-1 text-muted-foreground/50">
                      {levelFilter !== 'all' && `Level: ${levelFilter}`}
                      {levelFilter !== 'all' && searchQuery && ' + '}
                      {searchQuery && `Search: "${searchQuery}"`}
                    </p>
                    <button
                      type="button"
                      onClick={() => { setLevelFilter('all'); setSearchQuery(''); }}
                      className="tap text-xs text-primary hover:text-primary/80 mt-3 underline"
                    >
                      Clear filters
                    </button>
                  </div>
                ) : (
                  <div className="text-muted-foreground/60 text-center py-16">
                    <Terminal className="h-12 w-12 mx-auto mb-3 opacity-30" />
                    <p className="text-sm">Waiting for automation logs...</p>
                    <p className="text-xs mt-2 text-muted-foreground/50">
                      Logs from automation workers will stream here in real-time
                    </p>
                  </div>
                )}
              </div>
            </ScrollArea>

            {/* Autoscroll paused indicator */}
            {!autoScroll && logs.length > 0 && (
              <div className="flex flex-wrap items-center justify-center gap-2 border-t border-border bg-secondary/60 px-4 py-1.5">
                <Pause className="h-3 w-3 text-warning" />
                <span className="text-[10px] text-warning">Autoscroll paused</span>
                <button
                  type="button"
                  onClick={() => { setAutoScroll(true); logsEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }}
                  className="text-[10px] text-primary hover:text-primary/80 underline ml-2"
                  data-testid="resume-autoscroll-btn"
                >
                  Resume & jump to bottom
                </button>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ==================== Main Component ====================

export default function PhoneMonitorPage() {
  usePageTitle('Phone Monitoring');
  const [expandedDevice, setExpandedDevice] = useState(null);
  const [perPage, setPerPage] = useState(loadPerPage);
  const [page, setPage] = useState(0);
  // Bumped after every streamManager.syncPage so StreamMounts re-attach once the
  // canvas/socket for their key actually exists.
  const [streamsTick, setStreamsTick] = useState(0);

  // Fetch presence from the Java backend (which PCs are connected + adb devices they see).
  const { data: detectedDevicesData, isLoading: isLoadingDevices, refetch: refetchDevices } = useQuery({
    queryKey: ['bm-presence'],
    queryFn: () => bmPresenceApi.list().then(res => res.data),
    refetchInterval: 5000,
  });

  // Flatten devices across all PCs into one paginated grid, carrying install + PC identity.
  // Memoised on the presence response. That response is a NEW object on nearly every 5 s poll
  // (each PC's `lastSeen` is its latest heartbeat), so anything keyed on allDevices is rebuilt every
  // poll - which is why the heavy device/slot index below does NOT depend on it.
  const allDevices = React.useMemo(() => (detectedDevicesData?.clients || []).flatMap((client) =>
    (client.devices || []).map(device => ({
      ...device,
      installId: client.installId,
      hostname: client.hostname || client.label,
      // The PC's own hostname, which is what the server stamps into a device row's last_seen_pc.
      // Kept apart from `hostname` above, which falls back to the install's label for display.
      pc: client.hostname || null,
      client_connected: client.client_connected,
    }))
  ), [detectedDevicesData]);

  /*
   * Which device row each phone is, and which slots run on it. Fetched ONCE per page load and on
   * Refresh - never on the 5 s presence poll: assignments change on the Slots/Devices pages, not
   * while someone watches screens, and /uiapi/slots on the largest account is 10,100 rows. These are
   * the same query keys the Slots, Devices and Dashboard pages use, so arriving from one of them
   * usually paints from cache at once. The tiles never wait on this: presence alone drives the
   * grid and the streams, and each tile shows a placeholder line until these answer.
   */
  const deviceRowsQuery = useQuery({
    queryKey: ['devices'],
    queryFn: () => devicesApi.list().then(res => res.data),
  });
  const slotRowsQuery = useQuery({
    queryKey: ['slots'],
    queryFn: () => slotsApi.list().then(res => res.data),
  });
  // A failed fetch is otherwise silent (react-query keeps it to itself); say why a tile reads
  // "device info unavailable" / "slots unavailable", once per failure.
  const deviceRowsError = deviceRowsQuery.isError ? deviceRowsQuery.error : null;
  const slotRowsError = slotRowsQuery.isError ? slotRowsQuery.error : null;
  useEffect(() => {
    if (deviceRowsError) {
      console.warn('[monitor] GET /uiapi/devices failed - tiles cannot name their device row:',
        deviceRowsError?.response?.status || deviceRowsError?.message || deviceRowsError);
    }
  }, [deviceRowsError]);
  useEffect(() => {
    if (slotRowsError) {
      console.warn('[monitor] GET /uiapi/slots failed - tiles cannot list their slots:',
        slotRowsError?.response?.status || slotRowsError?.message || slotRowsError);
    }
  }, [slotRowsError]);
  const loadState = (q) => (q.isError ? LOAD.ERROR : q.isSuccess ? LOAD.READY : LOAD.LOADING);
  const deviceRowsState = loadState(deviceRowsQuery);
  const slotRowsState = loadState(slotRowsQuery);
  // Two memos on purpose: the row index (up to 10,100 slots, ~14 ms) is keyed ONLY on the devices
  // and slots responses; the presence half (which PCs report which serial) is the cheap one and is
  // the only part rebuilt on the 5 s poll.
  const rowIndex = React.useMemo(() => buildRowIndex({
    devices: deviceRowsQuery.data,
    slots: slotRowsQuery.data,
    devicesState: deviceRowsState,
    slotsState: slotRowsState,
  }), [deviceRowsQuery.data, slotRowsQuery.data, deviceRowsState, slotRowsState]);
  const pcsBySerial = React.useMemo(() => buildPresenceIndex(allDevices), [allDevices]);
  const monitorIndex = React.useMemo(() => ({ ...rowIndex, pcsBySerial }), [rowIndex, pcsBySerial]);

  const [refreshing, setRefreshing] = useState(false);
  const refetchDeviceRows = deviceRowsQuery.refetch;
  const refetchSlotRows = slotRowsQuery.refetch;

  const onlineDevices = allDevices.filter(d => d.state === 'device');

  // ----- Pagination -----
  const totalPages = Math.max(1, Math.ceil(allDevices.length / perPage));
  const safePage = Math.min(page, totalPages - 1);
  const pageStart = safePage * perPage;
  const pageDevices = allDevices.slice(pageStart, pageStart + perPage);

  // The phones on THIS page that actually stream (online only). installId is carried so the REMOTE
  // transport can address Spring's per-install viewer socket; it is harmless for local mode. `pc`
  // is the PC's name for the tile's words and the [monitor] log lines.
  const pageStreamEntries = pageDevices
    .filter(d => d.state === 'device')
    .map(d => ({ key: keyOf(d), serial: d.serial, installId: d.installId, pc: d.hostname || '' }));

  // Keep a live ref of the current entries for the visibility handler.
  const entriesRef = useRef(pageStreamEntries);
  entriesRef.current = pageStreamEntries;

  // A signature that changes only when the SET of streaming phones on the page changes.
  const pageSignature = pageStreamEntries.map(e => e.key).sort().join('|');

  /*
   * ==================== Video route + self-heal (design V1-V6) ====================
   *
   * V1: the route is resolved at RUN time (lib/monitorConfig.js): URL, then this browser, then the
   * server's GET /uiapi/bm/video/config (the fleet rollback), then the build, then the default. With
   * `auto` (the owner's route) a ~1 s probe of ws://localhost:6847 picks direct, else the server.
   * NO socket opens before that resolves (review M4): the page says "Loading video settings..." for
   * at most 3 s (+1 s probe), then applies the fallback. Every change of route bumps `epoch`, which
   * stops every socket and reopens the page on the new route.
   */
  const [video, setVideo] = useState({ ready: false, cfg: null, route: null, autoFallback: false, probe: null, epoch: 0 });
  const videoRef = useRef(video);
  videoRef.current = video;
  const mountedRef = useRef(true);
  const [idlePaused, setIdlePaused] = useState(false);
  const idleRef = useRef(false);
  idleRef.current = idlePaused;
  const [repairUntil, setRepairUntil] = useState({});
  const repairUntilRef = useRef({});
  const lastReopenRef = useRef(0);
  const lastSyncKeyRef = useRef(null);
  const lastInputRef = useRef(Date.now());

  const resolveVideo = useCallback(async (why) => {
    const t0 = Date.now();
    const cfg = await resolveMonitorConfig({
      search: typeof window !== 'undefined' ? window.location.search : '',
      fetchServerConfig: () => bmVideoApi.config(CONFIG_TIMEOUT_MS),
      envTransport: envTransport(),
      nodeEnv: process.env.NODE_ENV,
    });
    const r = await chooseRoute(cfg.transport, () => probeLocalRelay({ base: WSSCRCPY_BASE, timeoutMs: cfg.autoProbeMs }));
    if (!mountedRef.current) return;
    setResolvedTransport(r.route);
    const srv = cfg.server || {};
    console.info(`[monitor] video settings (${why}): transport=${cfg.transport} [${cfg.sources.transport}]`
      + (r.probe ? `, probe of ${WSSCRCPY_BASE}: ${r.probe.ok ? 'answered' : 'no answer'} (${r.probe.why}, ${r.probe.ms} ms)` : '')
      + ` -> route ${r.route === 'remote' ? 'through the server' : 'direct'}${r.autoFallback ? ' (a phone not on this PC falls back to the server)' : ''};`
      + ` reconnect=${cfg.reconnect} states=${cfg.states} repair=${cfg.repair} hold=${cfg.hold} reprobe=${cfg.reprobe};`
      + ` server config: ${srv.status}${srv.httpStatus ? ` HTTP ${srv.httpStatus}` : ''}${srv.detail ? ` (${srv.detail})` : ''}`
      + (cfg.overrides.length ? `; set in this browser: ${cfg.overrides.map(o => `${o.key}=${o.value}`).join(', ')}` : '')
      + `; ${Date.now() - t0} ms`);
    setVideo((prev) => ({ ready: true, cfg, route: r.route, autoFallback: r.autoFallback, probe: r.probe, epoch: prev.epoch + 1 }));
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    resolveVideo('page opened');
    return () => {
      mountedRef.current = false;
      setResolvedTransport(null);
    };
  }, [resolveVideo]);

  /** The sync options for the CURRENT resolved route (read at call time: review M4). */
  const getSyncOpts = useCallback(() => {
    const v = videoRef.current;
    const cfg = v.cfg || {};
    return {
      transport: v.route || 'local',
      apiBase: SPRING_API_BASE,
      reconnect: !!cfg.reconnect,
      autoFallback: !!v.autoFallback,
      remoteLow: cfg.remoteLow,
      remoteHigh: cfg.remoteHigh,
      stagger: !!cfg.reconnect,
      // MF7: hold a silent socket instead of reconnecting; AUTO re-probe of the direct route for server-route tiles.
      hold: !!cfg.hold,
      reprobe: !!cfg.reprobe && cfg.transport === 'auto',
    };
  }, []);

  // MF7 AUTO re-probe: a tile switched to the direct route (after its first direct frame). Its canvas was swapped in place;
  // re-home canvases, and if the page itself was on the server route, new tiles now start direct (with the fallback) too.
  useEffect(() => {
    streamManager.configure({
      onSwap: () => {
        setStreamsTick((x) => x + 1);
        const v = videoRef.current;
        if (v.ready && v.cfg && v.cfg.transport === 'auto' && v.route === 'remote') {
          console.info('[monitor] re-probe: a phone streams direct from this PC now - new tiles open direct first'
            + ' (a phone on another PC falls back to the server)');
          setResolvedTransport('local');
          setVideo((prev) => ({ ...prev, route: 'local', autoFallback: true, probe: { ok: true, why: 're-probe', ms: 0 } }));
        }
      },
    });
    return () => streamManager.configure({ onSwap: null });
  }, []);

  // PAGE CHANGE: open sockets for this page's phones, close sockets for phones that
  // left the page. This is the ONLY place sockets start/stop on navigation. No
  // IntersectionObserver: every phone on the page streams continuously at LOW.
  // Self-heal: nothing opens before the video settings resolve, and a new `epoch` (route change,
  // Refresh, Reset) first stops every socket so none is left on the old route.
  useEffect(() => {
    if (!WEBCODECS_OK || !video.ready || idlePaused) return;
    const key = String(video.epoch);
    if (lastSyncKeyRef.current !== null && lastSyncKeyRef.current !== key) streamManager.stopAll();
    lastSyncKeyRef.current = key;
    streamManager.syncPage(entriesRef.current, WSSCRCPY_BASE, getSyncOpts());
    setStreamsTick(t => t + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageSignature, video.ready, video.epoch, idlePaused]);

  // Stop every stream when the whole page unmounts (leaving Phone Monitoring).
  useEffect(() => {
    return () => streamManager.stopAll();
  }, []);

  // TAB FOCUS: freeze all sockets when the tab is hidden (debounced so a quick
  // alt-tab is a no-op), resume the current page on return - on the route resolved NOW, not the
  // one this handler saw at mount (review M4).
  useEffect(() => {
    if (!WEBCODECS_OK) return undefined;
    const detach = attachVisibilityAutostop(
      streamManager,
      () => entriesRef.current,
      {
        base: WSSCRCPY_BASE,
        getSyncOpts,
        canResume: () => videoRef.current.ready && !idleRef.current,
        debounceMs: 400,
        onChange: () => setStreamsTick(t => t + 1),
      },
    );
    return detach;
  }, [getSyncOpts]);

  // Signed out? A signed-out browser's server-route socket fails at the handshake (HTTP 401), which
  // the browser reports as a bare 1006. After two in a row a tile asks; one authenticated call per
  // minute at most decides, and a 401 stops every tile with "Signed out, sign in again".
  useEffect(() => {
    let lastCheck = 0;
    streamManager.configure({
      onAuthSuspect: () => {
        const t = Date.now();
        if (t - lastCheck < AUTH_CHECK_GAP_MS) return;
        lastCheck = t;
        console.warn('[monitor] two server-route sockets in a row closed before opening - checking the sign-in');
        Promise.resolve()
          .then(() => bmVideoApi.config(CONFIG_TIMEOUT_MS))
          .then(
            () => console.info('[monitor] the sign-in is fine; those closes have another cause'),
            (err) => {
              const s = err && err.response ? err.response.status : null;
              if (s === 401) {
                console.warn('[monitor] signed out - every tile stops');
                streamManager.giveUpAll('signed-out', 'Signed out, sign in again');
              } else {
                console.info(`[monitor] the sign-in check was inconclusive (${s || (err && err.message) || 'error'})`);
              }
            },
          );
      },
    });
    return () => streamManager.configure({ onAuthSuspect: null });
  }, []);

  // ----- Repair (design V4). Rides the PC's own log uplink; the server rate-limits it too. -----
  const setCooldown = useCallback((installId, until) => {
    repairUntilRef.current = { ...repairUntilRef.current, [installId]: until };
    setRepairUntil(repairUntilRef.current);
  }, []);

  const requestRepair = useCallback(async (installId, pc, serials, source, press) => {
    const name = pc || `PC ${installId}`;
    const t = Date.now();
    const until = repairUntilRef.current[installId] || 0;
    if (until > t) {
      const left = Math.ceil((until - t) / 1000);
      console.info(`[monitor] repair video on ${name} skipped (${source}): repaired less than a minute ago, ${left} s left`);
      toast.warning(`${name}: repaired moments ago, try again in ${left} s`);
      return;
    }
    const cfg = videoRef.current.cfg || {};
    setCooldown(installId, t + (cfg.repairCooldownSec || 60) * 1000);
    console.info(`[monitor] repair video on ${name} (install ${installId}, ${source}${press ? ` ${press}` : ''}): serials=[${serials.join(', ')}]`);
    try {
      const res = await bmVideoApi.repair(installId, serials, press);
      const d = (res && res.data) || {};
      console.info(`[monitor] repair ${name}: HTTP ${res && res.status} ok=${d.ok} "${d.result || ''}" in ${Date.now() - t} ms`);
      toast.success(`${name}: ${d.result || 'video repaired'}`);
    } catch (err) {
      const status = err && err.response ? err.response.status : null;
      const d = (err && err.response && err.response.data) || {};
      console.warn(`[monitor] repair ${name}: ${status ? `HTTP ${status}` : 'no answer'} `
        + `${d.result || d.detail || (err && err.message) || ''} in ${Date.now() - t} ms`);
      if (status === 429 && d.retryAfterSec) setCooldown(installId, Date.now() + d.retryAfterSec * 1000);
      // Nothing was repaired on these, so the PC may be asked again at once.
      if (!status || status === 400 || status === 401 || status === 404 || status === 409) setCooldown(installId, 0);
      const msg = repairErrorText(status, d, name);
      if (status === 429 || status === 502) toast.warning(msg);
      else toast.error(msg);
    }
  }, [setCooldown]);

  /** Re-probe the AUTO route and reopen every stream with a fresh budget (a new epoch). */
  const reopenStreams = useCallback(async (why) => {
    const v = videoRef.current;
    if (!v.ready || !v.cfg) return;
    streamManager.stopAll();
    let r = { route: v.route, autoFallback: v.autoFallback, probe: v.probe };
    if (v.cfg.transport === 'auto') {
      r = await chooseRoute('auto', () => probeLocalRelay({ base: WSSCRCPY_BASE, timeoutMs: v.cfg.autoProbeMs }));
      console.info(`[monitor] ${why}: probe ${r.probe && r.probe.ok ? 'answered' : 'no answer'}`
        + ` (${r.probe && r.probe.why}, ${r.probe && r.probe.ms} ms) -> route ${r.route === 'remote' ? 'through the server' : 'direct'}`);
    }
    if (!mountedRef.current) return;
    setResolvedTransport(r.route);
    setVideo((prev) => ({ ...prev, route: r.route, autoFallback: r.autoFallback, probe: r.probe, epoch: prev.epoch + 1 }));
  }, []);

  /*
   * Refresh (design V4): the three list refetches, as before; and with `repair` on, also
   *   - every stream on the page reopened (stopAll + resubscribe, fresh budgets; at most every 5 s);
   *   - one "repair video" request per PC that has a tile without a picture, in parallel.
   * A page whose every tile streams sends no repair, so a healthy relay is never restarted for nothing.
   */
  const handleRefresh = useCallback(() => {
    setRefreshing(true);
    Promise.allSettled([refetchDevices(), refetchDeviceRows(), refetchSlotRows()])
      .finally(() => setRefreshing(false));
    const v = videoRef.current;
    if (!WEBCODECS_OK || !v.ready || !v.cfg || !v.cfg.repair) return;
    const entries = entriesRef.current;
    // Which PCs need a repair is decided on the states BEFORE the reopen.
    const byInstall = new Map();
    // MF7: why a tile gets no repair, for an honest log line (a phone whose screen is off is not "streaming")
    const skipped = { streaming: 0, screenOff: 0, other: 0 };
    for (const e of entries) {
      const st = streamManager.getStatus(e.key);
      if (!wantsRepair(st)) {
        if (st && st.state === 'streaming') skipped.streaming++;
        else if (st && st.note && String(st.note.code || '').toUpperCase() === 'SCREEN_OFF') skipped.screenOff++;
        else skipped.other++;
        continue;
      }
      if (!byInstall.has(e.installId)) byInstall.set(e.installId, { pc: e.pc, serials: [], keys: [] });
      byInstall.get(e.installId).serials.push(e.serial);
      byInstall.get(e.installId).keys.push(e.key);
    }
    if (idleRef.current) {
      lastInputRef.current = Date.now();
      setIdlePaused(false);
    }
    const t = Date.now();
    let reopened = null;
    if (t - lastReopenRef.current >= REOPEN_DEBOUNCE_MS) {
      lastReopenRef.current = t;
      console.info(`[monitor] Refresh: reopening ${entries.length} stream(s) with fresh retry budgets`);
      reopened = reopenStreams('Refresh');
    } else {
      console.info('[monitor] Refresh: the streams were reopened less than 5 s ago - not again');
    }
    if (byInstall.size === 0) {
      if (entries.length && !skipped.screenOff && !skipped.other) {
        console.info('[monitor] Refresh: every tile streams - no repair sent');
      } else if (entries.length) {
        console.info(`[monitor] Refresh: no repair sent - ${skipped.streaming} tile(s) streaming, ${skipped.screenOff} whose PC says `
          + `the phone's screen is off (a repair cannot turn it on), ${skipped.other} stopped for a reason a repair cannot fix`);
      }
      return;
    }
    // One id per press: the server counts one Refresh as ONE repair against the per-user hourly limit.
    const press = `p-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
    if (!reopened) {
      for (const [installId, info] of byInstall) requestRepair(installId, info.pc, info.serials, 'Refresh', press);
      return;
    }
    // Review MF3: each PC's repair goes out once ITS reopened tiles have opened (the new sockets subscribed the phones on
    // that PC), or at the latest REPAIR_AFTER_OPEN_MAX_MS after the reopen - never in the same tick as the reopen.
    Promise.resolve(reopened).catch(() => {}).then(() => {
      for (const [installId, info] of byInstall) {
        whenTilesOpen(info.keys, t, REPAIR_AFTER_OPEN_MAX_MS).then((why) => {
          if (!mountedRef.current) return;
          console.info(`[monitor] Refresh: repair video on ${info.pc || `PC ${installId}`} after ${Date.now() - t} ms `
            + `(${why === 'open' ? 'its reopened tiles are open' : `not every reopened tile opened within ${REPAIR_AFTER_OPEN_MAX_MS} ms`})`);
          requestRepair(installId, info.pc, info.serials, 'Refresh', press);
        });
      }
    });
  }, [refetchDevices, refetchDeviceRows, refetchSlotRows, reopenStreams, requestRepair]);

  /** A tile's "Repair video on {pc}": that tile retries now and its PC is asked to repair. */
  const repairTile = useCallback((device) => {
    const key = keyOf(device);
    const t = Date.now();
    streamManager.retry(key);
    // Review MF3: the same order as Refresh - the repair follows the tile's reopened socket.
    whenTilesOpen([key], t, REPAIR_AFTER_OPEN_MAX_MS).then((why) => {
      if (!mountedRef.current) return;
      console.info(`[monitor] repair video on ${device.hostname || `PC ${device.installId}`} (tile ${device.serial}) after `
        + `${Date.now() - t} ms (${why === 'open' ? 'the tile reopened' : 'the tile did not reopen in time'})`);
      requestRepair(device.installId, device.hostname, [device.serial], 'tile');
    });
  }, [requestRepair]);

  // ----- Idle pause (review M5), with the reconnect switch -----
  const idleEnabled = !!(video.ready && video.cfg && video.cfg.reconnect);
  useEffect(() => {
    if (!idleEnabled || !WEBCODECS_OK) return undefined;
    lastInputRef.current = Date.now();
    const onInput = () => { lastInputRef.current = Date.now(); };
    // Time spent hidden is not idleness at a visible page.
    const onVis = () => { if (document.visibilityState === 'visible') lastInputRef.current = Date.now(); };
    const events = ['pointerdown', 'pointermove', 'keydown', 'wheel', 'touchstart'];
    events.forEach((ev) => window.addEventListener(ev, onInput, { passive: true }));
    document.addEventListener('visibilitychange', onVis);
    const id = setInterval(() => {
      if (idleRef.current || document.visibilityState === 'hidden') return;
      if (Date.now() - lastInputRef.current < IDLE_PAUSE_MS) return;
      console.info('[monitor] no pointer or key input for 30 min - every stream paused to save bandwidth (click to resume)');
      streamManager.stopAll();
      setIdlePaused(true);
      setStreamsTick(x => x + 1);
    }, IDLE_CHECK_MS);
    return () => {
      events.forEach((ev) => window.removeEventListener(ev, onInput));
      document.removeEventListener('visibilitychange', onVis);
      clearInterval(id);
    };
  }, [idleEnabled]);

  const resumeFromIdle = useCallback(() => {
    lastInputRef.current = Date.now();
    console.info('[monitor] resumed after the idle pause');
    setIdlePaused(false);
  }, []);

  // ----- Support logging (design V6): a page summary every 30 s, and window.__necroMonitor -----
  const statsCtxRef = useRef({});
  statsCtxRef.current = { page: safePage + 1, online: onlineDevices.length };
  useEffect(() => {
    if (!video.ready || !WEBCODECS_OK) return undefined;
    let prev = new Map();
    let prevAt = Date.now();
    const id = setInterval(() => {
      const t = Date.now();
      const dt = Math.max(1, (t - prevAt) / 1000);
      const counts = {};
      let frames = 0;
      let bytes = 0;
      const next = new Map();
      for (const r of streamManager.stats()) {
        const st = streamManager.getStatus(r.key);
        const s = st ? st.state : r.state;
        counts[s] = (counts[s] || 0) + 1;
        const p = prev.get(r.key);
        if (p && s === 'streaming') {
          frames += Math.max(0, r.frames - p.frames);
          bytes += Math.max(0, r.videoBytes - p.videoBytes);
        }
        next.set(r.key, { frames: r.frames, videoBytes: r.videoBytes });
      }
      prev = next;
      prevAt = t;
      const streaming = counts.streaming || 0;
      const fps = streaming ? (frames / dt / streaming).toFixed(1) : '0';
      const kbps = streaming ? Math.round((bytes * 8) / 1000 / dt / streaming) : 0;
      const c = statsCtxRef.current;
      console.info(`[monitor] page ${c.page}: ${c.online} online, ${streaming} streaming (${fps} fps, ${kbps} kbps avg), `
        + `${counts.connecting || 0} connecting, ${(counts.waiting || 0) + (counts.starting || 0)} starting, `
        + `${counts['no-picture'] || 0} without picture, ${counts.retrying || 0} retrying, ${counts.paused || 0} off`);
    }, STATS_LOG_MS);
    return () => clearInterval(id);
  }, [video.ready]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    window.__necroMonitor = {
      stats: () => streamManager.stats().map((s) => ({ ...s, status: streamManager.getStatus(s.key) })),
      config: () => {
        const v = videoRef.current;
        return { ready: v.ready, route: v.route, autoFallback: v.autoFallback, probe: v.probe, epoch: v.epoch, ...(v.cfg || {}) };
      },
    };
    return () => {
      try { delete window.__necroMonitor; } catch (e) { window.__necroMonitor = undefined; }
    };
  }, []);

  /** Reset: forget every override stored in this browser, then resolve again from the server. */
  const resetOverrides = useCallback(() => {
    clearStoredOverrides();
    try {
      const u = new URL(window.location.href);
      ['transport', 'reconnect', 'states', 'repair', 'hold', 'reprobe'].forEach((k) => u.searchParams.delete(k));
      window.history.replaceState(window.history.state, '', u.pathname + u.search + u.hash);
    } catch (e) { /* ignore */ }
    console.info('[monitor] video settings stored in this browser were reset');
    streamManager.stopAll();
    resolveVideo('overrides reset');
  }, [resolveVideo]);

  const videoStates = !!(video.ready && video.cfg && video.cfg.states);
  const videoForTiles = React.useMemo(() => ({
    states: videoStates,
    repair: !!(video.ready && video.cfg && video.cfg.repair),
    idlePaused,
    repairUntil,
    onRepair: repairTile,
    onResume: resumeFromIdle,
  }), [videoStates, video.ready, video.cfg, idlePaused, repairUntil, repairTile, resumeFromIdle]);

  // Clamp page if the device list shrinks.
  useEffect(() => {
    if (page > totalPages - 1) setPage(Math.max(0, totalPages - 1));
  }, [totalPages, page]);

  const handleDeviceExpand = useCallback((device) => {
    setExpandedDevice(device);
  }, []);

  const handleCloseExpanded = useCallback(() => {
    setExpandedDevice(null);
  }, []);

  /* Tile size. Per-viewer taste, not fleet state, so it lives in localStorage rather than on the
     server - and a browser that refuses storage just gets the default back. */
  const [phoneW, setPhoneW] = useState(() => {
    try {
      const v = parseInt(window.localStorage.getItem(LS_PHONE_W), 10);
      // Snap to the nearest stop: a width stored before the stops changed is still meaningful.
      if (v > 0) {
        return PHONE_W_STOPS.reduce((best, w) =>
          (Math.abs(w - v) < Math.abs(best - v) ? w : best), PHONE_W_STOPS[0]);
      }
    } catch (e) { /* ignore */ }
    return PHONE_W;
  });
  // The stored value is the WIDTH, not the slider position, so re-tuning the stops above cannot
  // silently resize everyone's grid.
  const phoneWIndex = Math.max(0, PHONE_W_STOPS.indexOf(phoneW));
  const handlePhoneWChange = useCallback((v) => {
    const i = Array.isArray(v) ? v[0] : v;
    const n = PHONE_W_STOPS[i];
    if (n === undefined) return;
    setPhoneW(n);
    try { window.localStorage.setItem(LS_PHONE_W, String(n)); } catch (e) { /* ignore */ }
  }, []);

  const handlePerPageChange = useCallback((v) => {
    setPerPage(v);
    setPage(0);
    try { window.localStorage.setItem(LS_PER_PAGE, String(v)); } catch (e) { /* ignore */ }
  }, []);

  // The phone grid: two columns on a phone, then as many columns of at least --phone-w as fit.
  const gridClass = 'grid grid-cols-2 gap-3 sm:gap-4 sm:grid-cols-[repeat(auto-fill,minmax(var(--phone-w),1fr))]';
  const gridStyle = { '--phone-w': `${phoneW}px` };

  return (
    <div className="space-y-5" data-testid="phone-monitor-page">
      <PageHeader
        icon={MonitorPlay}
        title="Phone Monitoring"
        description={onlineDevices.length > 0
          ? (videoStates
            // Honest count (V3): tiles that show a picture, not sockets opened.
            ? <VideoCounts online={onlineDevices.length} entries={pageStreamEntries} />
            : `${onlineDevices.length} device${onlineDevices.length > 1 ? 's' : ''} online · ${pageStreamEntries.length} streaming on this page`)
          : 'No devices streaming'}
        actions={(
          <>
            {/* Tile size. Hidden on phones: the grid is locked to 2 columns there, so the slider
                would move nothing. */}
            <div className="hidden items-center gap-2 sm:flex" data-testid="phone-size-control">
              <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Size</span>
              <Slider
                value={[phoneWIndex]}
                min={0}
                max={PHONE_W_STOPS.length - 1}
                step={1}
                onValueChange={handlePhoneWChange}
                className="w-28"
                aria-label="Phone tile size"
                data-testid="phone-size-slider"
              />
              {/* A visible readout, because the slider's own aria-valuenow is the STOP INDEX and
                  means nothing to anyone. Radix does not forward aria-valuetext from the root to
                  the thumb, so this is both the human label and the accessible one. */}
              <span className="w-10 shrink-0 text-xs tabular-nums text-muted-foreground" data-testid="phone-size-value">
                {phoneW}px
              </span>
            </div>

            {/* Phones-per-page selector */}
            <div className="flex items-center gap-2" data-testid="perpage-control">
              <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Per page</span>
              <div className="inline-flex rounded-full bg-secondary/70 p-1 ring-1 ring-border" role="group" aria-label="Phones per page">
                {PER_PAGE_OPTIONS.map((opt) => (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => handlePerPageChange(opt)}
                    aria-pressed={perPage === opt}
                    className={cn(
                      'tap rounded-full px-3 py-1.5 text-xs font-medium tnum transition-colors',
                      perPage === opt ? 'bg-primary text-primary-foreground shadow-glow' : 'text-muted-foreground hover:text-foreground'
                    )}
                    data-testid={`perpage-${opt}`}
                  >
                    {opt}
                  </button>
                ))}
              </div>
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={isLoadingDevices || refreshing}
              className="tap rounded-full"
              data-testid="refresh-btn"
            >
              <RefreshCw className={cn('h-4 w-4', (isLoadingDevices || refreshing) && 'animate-spin text-primary')} />
              Refresh
            </Button>
          </>
        )}
      />

      {/* Video route notes (V1): while the settings resolve, the route in use, and any override
          stored in this browser (visible, with Reset, so a support instruction never pins a
          browser silently). With every switch off and no override, nothing is drawn here. */}
      {WEBCODECS_OK && (!video.ready || videoStates || (video.cfg && video.cfg.overrides.length > 0)) && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground" data-testid="video-notes">
          {!video.ready && <span data-testid="video-settings-loading">Loading video settings...</span>}
          {video.ready && videoStates && (
            <span data-testid="video-route-note" data-route={video.route}>{routeWords(video)}</span>
          )}
          {video.ready && video.cfg && video.cfg.overrides.length > 0 && (
            <span data-testid="video-override-note">
              {describeOverrides(video.cfg.overrides)}
              {' - '}
              <button
                type="button"
                onClick={resetOverrides}
                className="font-medium text-primary underline hover:text-primary/80"
                data-testid="video-override-reset"
              >
                Reset
              </button>
            </span>
          )}
        </div>
      )}

      {idlePaused && (
        <button
          type="button"
          onClick={resumeFromIdle}
          className="flex w-full items-center gap-2 rounded-2xl border border-warning/30 bg-warning/10 px-4 py-3 text-left text-sm text-foreground/90"
          data-testid="video-idle-paused"
        >
          <Pause className="h-4 w-4 shrink-0 text-warning" />
          Paused to save bandwidth - click to resume
        </button>
      )}

      {!WEBCODECS_OK && (
        <div className="flex items-start gap-2 rounded-2xl border border-warning/30 bg-warning/10 px-4 py-3 text-sm text-foreground/90" role="alert">
          <AlertTriangle className="h-4 w-4 shrink-0 text-warning mt-0.5" />
          This browser has no WebCodecs support, so live phone screens are disabled. Logs still work.
        </div>
      )}

      {/* Loading State */}
      {isLoadingDevices && (
        <div className={gridClass} style={gridStyle} aria-busy="true" aria-label="Loading phones">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="min-w-0 w-full">
              <Skeleton className="w-full rounded-2xl bg-secondary/60" style={{ aspectRatio: PHONE_ASPECT }} />
              <Skeleton className="h-3 w-20 mx-auto mt-2 bg-secondary/60" />
            </div>
          ))}
        </div>
      )}

      {/* Devices Grid (flat, paginated) */}
      {!isLoadingDevices && allDevices.length > 0 && (
        <div className="space-y-4">
          <div className={gridClass} style={gridStyle} data-testid="phone-grid">
            {pageDevices.map((device) => (
              <PhoneCard
                key={keyOf(device)}
                device={device}
                assoc={associatePhone(device, monitorIndex)}
                streamsTick={streamsTick}
                onClick={() => handleDeviceExpand(device)}
                isExpanded={expandedDevice != null && keyOf(expandedDevice) === keyOf(device)}
                video={videoForTiles}
              />
            ))}
          </div>

          {/* Page controls */}
          {totalPages > 1 && (
            <div className="flex flex-wrap items-center justify-center gap-3 pt-2" data-testid="page-controls">
              <Button
                variant="outline"
                size="sm"
                disabled={safePage <= 0}
                onClick={() => setPage(p => Math.max(0, p - 1))}
                className="tap rounded-full"
                data-testid="page-prev"
              >
                <ChevronLeft className="h-4 w-4 mr-1" /> Prev
              </Button>
              <span className="text-sm text-muted-foreground tnum" data-testid="page-indicator">
                Page {safePage + 1} of {totalPages}
                <span className="text-muted-foreground/60"> · {allDevices.length} phones</span>
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={safePage >= totalPages - 1}
                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                className="tap rounded-full"
                data-testid="page-next"
              >
                Next <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          )}
        </div>
      )}

      {/* No clients */}
      {!isLoadingDevices && allDevices.length === 0 && (
        <div className="aurora-card aurora-card-dashed p-8 text-center sm:p-10" data-testid="monitor-empty">
          <span className="mx-auto inline-flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary ring-1 ring-primary/20">
            <MonitorPlay className="h-6 w-6" />
          </span>
          <h3 className="mt-4">No Client Apps Connected</h3>
          <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground text-pretty">
            Add a desktop client on the Devices page and run the NecroMancer client on your PC to
            detect connected phones and stream their logs here.
          </p>
          <Button asChild variant="outline" className="tap mt-5 rounded-full">
            <Link to="/devices"><Smartphone className="h-4 w-4" /> Open Devices</Link>
          </Button>
        </div>
      )}

      {/* Expanded View Modal */}
      <ExpandedPhoneView
        device={expandedDevice}
        assoc={expandedDevice ? associatePhone(expandedDevice, monitorIndex) : null}
        isOpen={!!expandedDevice}
        onClose={handleCloseExpanded}
        streamsTick={streamsTick}
        video={videoForTiles}
      />
    </div>
  );
}
